import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Activity, FileText, DollarSign, Bell, LogOut, User, Settings, Upload, Eye, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { doctorAPI, notificationAPI } from '../utils/api';
import CaseFilesViewer from './CaseFilesViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoctorDashboard({ user, onLogout }) {
  const [cases, setCases] = useState([]);
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileData, setProfileData] = useState({
    specialty: '',
    experience_years: '',
    license_number: '',
    price_per_case: '',
    bio: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchCases();
    fetchEarnings();
    fetchNotifications();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await doctorAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setShowProfileForm(true);
      }
    }
  };

  const fetchCases = async () => {
    try {
      const response = await doctorAPI.getCases();
      setCases(response.data);
    } catch (error) {
      console.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await doctorAPI.getEarnings();
      setEarnings(response.data);
    } catch (error) {
      console.error('Failed to load earnings');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      await doctorAPI.createProfile({
        ...profileData,
        experience_years: parseInt(profileData.experience_years),
        price_per_case: parseFloat(profileData.price_per_case),
      });
      
      // Upload photo if selected
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        await doctorAPI.uploadPhoto(formData);
      }
      
      toast.success('Profile submitted for approval');
      setShowProfileForm(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAcceptCase = async (caseId) => {
    try {
      await doctorAPI.acceptCase(caseId);
      toast.success('Case accepted');
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept case');
    }
  };

  const handleDeclineCase = async (caseId) => {
    try {
      await doctorAPI.declineCase(caseId);
      toast.success('Case declined');
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline case');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'in_review':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'waiting':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getStatusText = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (showProfileForm) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl p-8 bg-white rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Complete Your Profile</h2>
          <form onSubmit={handleCreateProfile} className="space-y-4">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-100 border-4 border-white shadow-lg">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-400 to-teal-600">
                      <User className="w-16 h-16 text-white" />
                    </div>
                  )}
                </div>
                <label 
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-teal-700 shadow-lg"
                >
                  <Upload className="w-5 h-5 text-white" />
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
            <p className="text-center text-sm text-slate-600 mb-4">Click the camera icon to upload your photo</p>
            
            <div>
              <Label htmlFor="specialty">Specialty</Label>
              <Input
                id="specialty"
                data-testid="specialty-input"
                value={profileData.specialty}
                onChange={(e) => setProfileData({ ...profileData, specialty: e.target.value })}
                placeholder="e.g., Neuroradiology, Chest Radiology"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="experience_years">Years of Experience</Label>
                <Input
                  id="experience_years"
                  data-testid="experience-input"
                  type="number"
                  value={profileData.experience_years}
                  onChange={(e) => setProfileData({ ...profileData, experience_years: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="price_per_case">Price Per Case ($)</Label>
                <Input
                  id="price_per_case"
                  data-testid="price-input"
                  type="number"
                  step="0.01"
                  value={profileData.price_per_case}
                  onChange={(e) => setProfileData({ ...profileData, price_per_case: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="license_number">Medical License Number</Label>
              <Input
                id="license_number"
                data-testid="license-input"
                value={profileData.license_number}
                onChange={(e) => setProfileData({ ...profileData, license_number: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="bio">Professional Bio</Label>
              <textarea
                id="bio"
                data-testid="bio-input"
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Brief description of your experience and expertise..."
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-profile-button"
              className="w-full bg-primary text-white hover:bg-primary/90 h-11 rounded-full"
            >
              Submit Profile for Approval
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (profile?.status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white rounded-xl shadow-lg text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-amber-600 animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <h2 className="text-2xl font-bold font-heading text-slate-900 mb-4">Profile Under Review</h2>
          <p className="text-slate-600 mb-6">
            Your profile is currently being reviewed by our admin team. You will be notified once it's approved.
          </p>
          <Button
            data-testid="logout-pending-button"
            onClick={onLogout}
            variant="outline"
            className="rounded-full"
          >
            Logout
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold font-heading text-slate-900">RadiologyConsult</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="notifications-button"
              className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => toast.info(`${notifications.filter(n => !n.read).length} unread notifications`)}
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"></span>
              )}
            </button>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-900">{user.full_name}</span>
            </div>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="ghost"
              className="text-slate-600 hover:text-slate-900"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <Routes>
        <Route path="/" element={
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Total Earnings</div>
                    <div className="text-3xl font-bold font-heading text-primary">
                      ${earnings?.total_earnings?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <DollarSign className="w-10 h-10 text-primary opacity-20" />
                </div>
              </Card>
              <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
                <div className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Active Cases</div>
                <div className="text-3xl font-bold font-heading text-slate-900">
                  {cases.filter(c => c.status === 'in_review').length}
                </div>
              </Card>
              <Card className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm">
                <div className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Completed Cases</div>
                <div className="text-3xl font-bold font-heading text-slate-900">
                  {earnings?.completed_cases || 0}
                </div>
              </Card>
            </div>

            {/* Cases List */}
            <div>
              <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Your Cases</h2>
              {loading ? (
                <div className="text-center py-12 text-slate-600">Loading cases...</div>
              ) : cases.length === 0 ? (
                <Card className="p-12 bg-white rounded-xl border border-slate-100 text-center">
                  <FileText className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-600">No cases assigned yet. Cases will appear here once patients select you.</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {cases.map((caseItem) => (
                    <Card
                      key={caseItem.id}
                      data-testid={`case-${caseItem.id}`}
                      className="p-6 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold font-heading text-slate-900">
                              {caseItem.exam_type}
                            </h3>
                            <Badge className={`${getStatusColor(caseItem.status)} px-2.5 py-0.5 rounded-full text-xs font-medium border`}>
                              {getStatusText(caseItem.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">Patient: {caseItem.patient_name}</p>
                          <p className="text-sm text-slate-500">Case ID: {caseItem.id}</p>
                          {caseItem.deadline && (
                            <p className="text-sm text-accent font-medium mt-1">
                              Deadline: {new Date(caseItem.deadline).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-primary">${caseItem.price}</div>
                          <div className="text-xs text-slate-500">
                            {new Date(caseItem.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {caseItem.notes && (
                        <p className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded-lg">
                          <strong>Patient Notes:</strong> {caseItem.notes}
                        </p>
                      )}

                      <div className="flex gap-3">
                        {caseItem.status === 'waiting' && (
                          <>
                            <Button
                              data-testid={`accept-case-${caseItem.id}`}
                              onClick={() => handleAcceptCase(caseItem.id)}
                              className="bg-primary text-white hover:bg-primary/90 rounded-full text-sm px-4 h-9"
                            >
                              Accept Case
                            </Button>
                            <Button
                              data-testid={`decline-case-${caseItem.id}`}
                              onClick={() => handleDeclineCase(caseItem.id)}
                              variant="outline"
                              className="rounded-full text-sm px-4 h-9"
                            >
                              Decline
                            </Button>
                          </>
                        )}
                        {caseItem.status === 'in_review' && (
                          <>
                            <Button
                              data-testid={`write-report-${caseItem.id}`}
                              onClick={() => navigate(`/doctor/report/${caseItem.id}`)}
                              className="bg-primary text-white hover:bg-primary/90 rounded-full text-sm px-4 h-9"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Write Report
                            </Button>
                            <Button
                              data-testid={`view-files-${caseItem.id}`}
                              onClick={() => navigate(`/doctor/files/${caseItem.id}`)}
                              variant="outline"
                              className="rounded-full text-sm px-4 h-9"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Files
                            </Button>
                          </>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        } />
        <Route path="/report/:caseId" element={<WriteReport onComplete={() => { fetchCases(); navigate('/doctor'); }} />} />
        <Route path="/files/:caseId" element={<CaseFilesViewer />} />
      </Routes>
    </div>
  );
}

function WriteReport({ onComplete }) {
  const [reportData, setReportData] = useState({
    findings: '',
    impression: '',
    recommendations: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const navigate = useNavigate();
  const caseId = window.location.pathname.split('/').pop();

  const handlePreview = async () => {
    if (!reportData.findings || !reportData.impression || !reportData.recommendations) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    
    setPreviewLoading(true);
    try {
      const response = await doctorAPI.previewReport({
        case_id: caseId,
        ...reportData
      });
      
      // Create blob URL for PDF download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ön izleme oluşturulamadı');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPreview = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = 'report_preview.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      await doctorAPI.createReport({ case_id: caseId, ...reportData });
      toast.success('Rapor başarıyla gönderildi');
      setShowPreview(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor gönderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button
        data-testid="back-to-cases-button"
        onClick={() => navigate('/doctor')}
        variant="ghost"
        className="mb-6 text-slate-600 hover:text-slate-900"
      >
        ← Back to Cases
      </Button>

      <Card className="p-8 bg-white rounded-xl border border-slate-100 shadow-sm">
        <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Write Case Report</h2>
        <div className="space-y-6">
          <div>
            <Label htmlFor="findings" className="text-slate-900 font-medium mb-2 block">Findings</Label>
            <textarea
              id="findings"
              data-testid="findings-input"
              value={reportData.findings}
              onChange={(e) => setReportData({ ...reportData, findings: e.target.value })}
              rows={6}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Detailed observations from the imaging study..."
            />
          </div>

          <div>
            <Label htmlFor="impression" className="text-slate-900 font-medium mb-2 block">Impression</Label>
            <textarea
              id="impression"
              data-testid="impression-input"
              value={reportData.impression}
              onChange={(e) => setReportData({ ...reportData, impression: e.target.value })}
              rows={4}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Summary and interpretation of findings..."
            />
          </div>

          <div>
            <Label htmlFor="recommendations" className="text-slate-900 font-medium mb-2 block">Recommendations</Label>
            <textarea
              id="recommendations"
              data-testid="recommendations-input"
              value={reportData.recommendations}
              onChange={(e) => setReportData({ ...reportData, recommendations: e.target.value })}
              rows={4}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Clinical recommendations and follow-up suggestions..."
            />
          </div>

          <Button
            type="button"
            data-testid="preview-report-button"
            onClick={handlePreview}
            disabled={previewLoading || !reportData.findings || !reportData.impression || !reportData.recommendations}
            className="w-full bg-primary text-white hover:bg-primary/90 h-11 rounded-full"
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewLoading ? 'Ön İzleme Oluşturuluyor...' : 'Ön İzleme ve Onayla'}
          </Button>
        </div>
      </Card>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Rapor Ön İzleme</h3>
                <p className="text-sm text-slate-500">Lütfen raporu kontrol edip onaylayın</p>
              </div>
              <button
                onClick={handleClosePreview}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Report Preview Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-4 border border-slate-200">
                <div className="text-center mb-4">
                  <h2 className="text-2xl font-bold text-teal-600">www.vtoroemnenie.com</h2>
                  <p className="text-sm text-slate-500">Medical Second Opinion Platform</p>
                  <div className="mt-2 py-1 px-3 bg-red-100 text-red-600 text-sm font-bold rounded inline-block">
                    *** ÖN İZLEME / PREVIEW ***
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-center text-slate-800 mb-4">
                  MEDICAL SECOND OPINION REPORT
                </h3>
              </div>
              
              {/* Report Sections */}
              <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200 space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Findings (Bulgular)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{reportData.findings}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Impression (İzlenim)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{reportData.impression}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Recommendations (Öneriler)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{reportData.recommendations}</p>
                </div>
              </div>
              
              {/* Info Note */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Not:</strong> Bu bir ön izlemedir. Onayladığınızda rapor PDF olarak oluşturulacak ve hasta bilgileri, doktor bilgileri ve platform logosu eklenecektir.
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handleDownloadPreview}
                className="rounded-full px-4"
                data-testid="download-preview-button"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF İndir
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClosePreview}
                  className="rounded-full px-6"
                  data-testid="cancel-preview-button"
                >
                  Düzenle
                </Button>
                <Button
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
                  data-testid="confirm-submit-button"
                >
                  {submitting ? 'Gönderiliyor...' : 'Onayla ve Gönder'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
